if (!customElements.get('load-more-products')) {
  customElements.define(
    'load-more-products',
    class LoadMoreProducts extends HTMLElement {
      /** @type {Map<number, string>} */
      pages = new Map();

      /** @type {(() => void) | null} */
      resolveNextPage = null;

      constructor() {
        super();

        this.container = document.querySelector('[js-load-more-products-container]');
      }

      connectedCallback() {
        const currentPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);

        if (currentPage > 1) {
          this.loadPreviousPages(currentPage);
        }

        if (!this.dataset.url) return;

        this.prefetchNextPage();

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.renderNextPage();
            });
          },
          { rootMargin: '0px 0px 300px 0px' },
        );

        observer.observe(this);
      }

      async loadPreviousPages(/** @type {number} */ currentPage) {
        for (let page = 1; page < currentPage; page++) {
          const html = await this.fetchPageHTML(page);
          if (!html || !this.container) continue;

          const firstEl = this.container.children[0];
          const oldTop = firstEl?.getBoundingClientRect().top ?? 0;

          this.container.insertAdjacentHTML('afterbegin', html);

          if (firstEl) {
            const delta = firstEl.getBoundingClientRect().top - oldTop;
            window.scrollBy({ top: delta, behavior: 'instant' });
          }
        }
      }

      async prefetchNextPage() {
        if (!this.url) return;

        const nextUrl = this.buildUrl(this.dataset.url ?? '');
        const response = await fetch(nextUrl).catch(console.error);
        if (!response) return;

        const nextPageNum = this.currentNextPageNumber();
        this.pages.set(nextPageNum, await response.text());

        this.resolveNextPage?.();
        this.resolveNextPage = null;
      }

      async renderNextPage() {
        if (!this.url || this.isLoading) return;

        this.setAttribute('loading', 'true');

        const pageNum = this.currentNextPageNumber();
        let rawHTML = this.pages.get(pageNum);

        if (!rawHTML) {
          await /** @type {Promise<void>} */ (
            new Promise((resolve) => {
              this.resolveNextPage = () => resolve();
            })
          );
          rawHTML = this.pages.get(pageNum);
        }

        if (!rawHTML) {
          this.removeAttribute('loading');
          return;
        }

        this.pages.delete(pageNum);

        const parsedHTML = new DOMParser().parseFromString(rawHTML, 'text/html');
        const products = parsedHTML.querySelector('.product-list')?.innerHTML ?? '';
        const loadMoreElement = /** @type {HTMLElement | null} */ (parsedHTML.querySelector('load-more-products'));

        this.container?.insertAdjacentHTML('beforeend', products);
        window.history.replaceState({}, '', /** @type {string} */ (this.url));

        if (loadMoreElement && loadMoreElement.dataset.url) {
          this.dataset.url = loadMoreElement.dataset.url;
          this.prefetchNextPage();
        } else {
          this.remove();
        }

        this.removeAttribute('loading');
      }

      async fetchPageHTML(/** @type {number} */ page) {
        const url = this.dataset.url ? new URL(location.origin + this.dataset.url) : new URL(location.href);
        url.searchParams.set('page', String(page));
        if (this.dataset.sectionId) url.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(url.pathname + '?' + url.searchParams).catch(console.error);
        if (!response) return null;

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return html.querySelector('.product-list')?.innerHTML ?? null;
      }

      buildUrl(/** @type {string} */ dataUrl) {
        const url = new URL(location.origin + dataUrl);
        if (this.dataset.sectionId) url.searchParams.set('section_id', this.dataset.sectionId);
        return url.pathname + '?' + url.searchParams;
      }

      currentNextPageNumber() {
        const url = new URL(location.origin + (this.dataset.url ?? location.pathname + location.search));
        return parseInt(url.searchParams.get('page') || '1', 10);
      }

      get url() {
        return this.dataset.url || false;
      }

      get isLoading() {
        return this.hasAttribute('loading');
      }
    },
  );
}